import { useCallback, useMemo, useReducer } from 'react';
import type { Reducer } from 'react';

import { ensureDefined } from '@e-socna/utils';
import { getFragmentData } from '@e-socna/data-layer';
import type {
  AccountingStateId,
  FolderStateId,
  FragmentType,
  MissionStateId,
  MissionWorkflowId,
  QuotationStateId,
  ReportStateId,
} from '@e-socna/data-layer';

import { useReferencesContext } from '../../ReferencesContext/hooks/useReferencesContext.hook';
import { isListedInItsFolder } from '../../../app/utils/mission-kind-profile.util';

import { NavigationContextActionType } from '../enums/action-type.enum';
import { NavigationContext_FolderSpaceInfo_Folder_Fragment } from '../fragments/folder.fragment';
import {
  NavigationContext_MissionSpaceCounts_Mission_Fragment,
  NavigationContext_MissionSpaceInfo_Mission_Fragment,
} from '../fragments/mission.fragment';
import { initialState } from '../types/state.type';
import type { Page } from '../enums/page.enum';
import type { NavigationContextAction } from '../types/actions.type';
import type { MissionSpaceCounts } from '../types/spaces.type';
import type { NavigationState } from '../types/state.type';

const buildMissionSpaceCounts = (
  pMission: FragmentType<
    typeof NavigationContext_MissionSpaceCounts_Mission_Fragment
  >,
): MissionSpaceCounts => {
  const mission = getFragmentData(
    NavigationContext_MissionSpaceCounts_Mission_Fragment,
    pMission,
  );

  return {
    businessEventsCount: mission.businessEventIds.length,
    filesCount: mission.fileDirectory.files.filter(
      (file) => !file.hideInFileExplorer,
    ).length,
    pricingTotalExcludingVat:
      mission.servicesInfo.totalCalculation?.result
        ?.discountedAmountExcludingVat ?? null,
    reportsCount: mission.reportInfos.length,
    subMissionsCount: mission.subMissionIds.length,
    toolkitsCount: mission.toolkitsInfo?.enabledToolkitIds?.length ?? 0,
  };
};

export const useNavigationReducer = () => {
  const referencesContext = useReferencesContext();

  const { defaultAccountingState, defaultQuotationState, defaultReportState } =
    ensureDefined({
      defaultAccountingState: referencesContext.defaultAccountingState,
      defaultQuotationState: referencesContext.defaultQuotationState,
      defaultReportState: referencesContext.defaultReportState,
    });

  const navigationReducer: Reducer<NavigationState, NavigationContextAction> = (
    state: NavigationState,
    action: NavigationContextAction,
  ) => {
    const actionType = action.type;

    switch (actionType) {
      case NavigationContextActionType.SetPage:
        return {
          ...state,
          page: action.payload,
        };
      case NavigationContextActionType.SetFolderSpaceInfo: {
        const payload = getFragmentData(
          NavigationContext_FolderSpaceInfo_Folder_Fragment,
          action.payload,
        );

        const folderSpaceInfo = {
          businessEventsCount: payload.businessEventIds.length,
          filesCount: payload.fileDirectory.files.filter(
            (file) => !file.hideInFileExplorer,
          ).length,
          folderId: payload._id,
          folderStateId: payload.stateId as FolderStateId,
          missionsCount: payload.missions.filter((mission) =>
            isListedInItsFolder(mission.kind),
          ).length,
        };

        return {
          ...state,
          folderSpaceInfo,
        };
      }
      case NavigationContextActionType.FlushFolderSpaceInfo:
        return {
          ...state,
          folderSpaceInfo: undefined,
        };
      case NavigationContextActionType.SetMissionSpaceInfo: {
        const payload = getFragmentData(
          NavigationContext_MissionSpaceInfo_Mission_Fragment,
          action.payload,
        );

        const missionSpaceInfo = {
          accountingStateId:
            (payload.accountingInfo?.stateId as
              AccountingStateId | undefined) ?? defaultAccountingState._id,
          compositeMission: payload.compositeMission
            ? {
                ...buildMissionSpaceCounts(payload.compositeMission),
                accountingStateId:
                  (payload.compositeMission.accountingInfo?.stateId as
                    AccountingStateId | undefined) ??
                  defaultAccountingState._id,
                missionId: payload.compositeMission._id,
                missionStateId: payload.compositeMission
                  .stateId as MissionStateId,
                quotationStateId:
                  (payload.compositeMission.quotationInfo?.stateId as
                    QuotationStateId | undefined) ?? defaultQuotationState._id,
              }
            : undefined,
          folderId: payload.folderId,
          kind: payload.kind,
          missionId: payload._id,
          missionStateId: payload.stateId as MissionStateId,
          missionWorkflowId: payload.workflowId as MissionWorkflowId,
          quotationStateId:
            (payload.quotationInfo?.stateId as QuotationStateId | undefined) ??
            defaultQuotationState._id,
          reportStateId:
            payload.reportInfos.length <= 1
              ? ((payload.reportInfos[0]?.stateId as
                  ReportStateId | undefined) ?? defaultReportState._id)
              : undefined,
          ...buildMissionSpaceCounts(payload),
        };

        return {
          ...state,
          missionSpaceInfo,
        };
      }
      case NavigationContextActionType.FlushMissionSpaceInfo:
        return {
          ...state,
          missionSpaceInfo: undefined,
        };
      default:
        throw new Error(
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `Unknown action type in NavigationContext reducer: ${actionType}`,
        );
    }
  };

  const [state, dispatch] = useReducer(navigationReducer, initialState);

  const setPage = useCallback((page: Page) => {
    dispatch({ type: NavigationContextActionType.SetPage, payload: page });
  }, []);

  const setFolderSpaceInfo = useCallback(
    (
      folderSpaceInfo: Readonly<
        FragmentType<typeof NavigationContext_FolderSpaceInfo_Folder_Fragment>
      >,
    ) => {
      dispatch({
        type: NavigationContextActionType.SetFolderSpaceInfo,
        payload: folderSpaceInfo,
      });
    },
    [],
  );

  const flushFolderSpaceInfo = useCallback(() => {
    dispatch({ type: NavigationContextActionType.FlushFolderSpaceInfo });
  }, []);

  const setMissionSpaceInfo = useCallback(
    (
      missionSpaceInfo: FragmentType<
        typeof NavigationContext_MissionSpaceInfo_Mission_Fragment
      >,
    ) => {
      dispatch({
        type: NavigationContextActionType.SetMissionSpaceInfo,
        payload: missionSpaceInfo,
      });
    },
    [],
  );

  const flushMissionSpaceInfo = useCallback(() => {
    dispatch({ type: NavigationContextActionType.FlushMissionSpaceInfo });
  }, []);

  return useMemo(
    () => ({
      navigation: state,
      dispatchNavigation: dispatch,
      setPage,
      setFolderSpaceInfo,
      flushFolderSpaceInfo,
      setMissionSpaceInfo,
      flushMissionSpaceInfo,
    }),
    [
      flushFolderSpaceInfo,
      flushMissionSpaceInfo,
      setFolderSpaceInfo,
      setMissionSpaceInfo,
      setPage,
      state,
    ],
  );
};
